import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Table,
  Button,
  Typography,
  Descriptions,
  Alert,
  Divider,
  Skeleton,
  Flex,
  Modal,
  Form,
  Input,
  message,
} from 'antd';
import {
  CheckCircleFilled,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import customerService from '../../services/customerService';
import { useCustomerRightRail, useCustomerRightRailMeta, useCustomerWorkspace } from '../../contexts/CustomerWorkspaceContext';
import WorkspaceEventsPanel, { workspaceEventSubscribeNoOp } from '../../components/workspaces/WorkspaceEventsPanel';
import PageHeader from '../../components/common/PageHeader';
import PageShell from '../../components/common/PageShell';
import TotalRow from '../../components/common/TotalRow';
import DocumentLineDescription from '../../components/common/DocumentLineDescription';
import { EstimateStatusBadge } from '../../components/common/StatusBadge';
import { formatCurrency, getCurrencyCode } from '../../utils/format';
import { isDueDateDisplayed } from '../../utils/documentDisplay';
import DocumentProgressSteps from '../../components/common/DocumentProgressSteps';
import DocumentDetailActionBar from '../../components/common/DocumentDetailActionBar';
import DocumentShareModal from '../../components/common/DocumentShareModal';

const { Title, Text, Paragraph } = Typography;

const EstimateDetailPage = () => {
  const { publicId } = useParams();
  const navigate = useNavigate();

  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [declineSubmitting, setDeclineSubmitting] = useState(false);
  const [declineForm] = Form.useForm();
  const [workspaceUnread, setWorkspaceUnread] = useState(0);

  const fetchEstimate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await customerService.getEstimate(publicId);
      setEstimate(data);
    } catch (err) {
      setError(err?.response?.status === 404
        ? 'Estimate not found.'
        : 'Failed to load estimate. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [publicId]);

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  const loadWorkspaceFeed = useCallback(
    () => customerService.getEstimateWorkspaceFeed(publicId),
    [publicId],
  );

  const postWorkspaceEvent = useCallback(
    (formData) => customerService.postEstimateEvent(publicId, formData),
    [publicId],
  );

  const postWorkspaceEventReply = useCallback(
    (payload) => {
      const fd = new FormData();
      fd.append('workspace_event_public_id', payload.workspaceEventPublicId);
      if (payload.body) fd.append('body', payload.body);
      (payload.files || []).forEach((f) => fd.append('files[]', f));
      return customerService.postEstimateEventReply(publicId, fd);
    },
    [publicId],
  );

  const workspaceEventsConfig = useMemo(
    () => ({
      canUpload: true,
      canComment: true,
      showCustomerVisibilityToggle: false,
      loadFeed: loadWorkspaceFeed,
      portalHandlers: { postWorkspaceEvent, postWorkspaceEventReply },
    }),
    [loadWorkspaceFeed, postWorkspaceEvent, postWorkspaceEventReply],
  );

  const workspaceRail = useMemo(
    () =>
      !publicId || error || !estimate ? null : (
        <WorkspaceEventsPanel
          entityPublicId={publicId}
          panelLayout="rail"
          subscribeWorkspaceEvent={workspaceEventSubscribeNoOp}
          config={workspaceEventsConfig}
          onUnreadCountChange={setWorkspaceUnread}
        />
      ),
    [publicId, error, estimate, workspaceEventsConfig],
  );

  useCustomerRightRail(workspaceRail);

  const panelMeta = useMemo(
    () => ({
      title: 'Workspace',
      timelineHref: publicId ? `/workspaces/estimate/${publicId}` : null,
      unreadCount: workspaceUnread,
    }),
    [publicId, workspaceUnread],
  );
  useCustomerRightRailMeta(panelMeta);

  const { rightPanelOpen, setRightPanelOpen, setMobileRailOpen } = useCustomerWorkspace();
  const handleOpenWorkspace = useCallback(() => {
    if (window.matchMedia('(min-width: 992px)').matches) {
      setRightPanelOpen((open) => !open);
    } else {
      setMobileRailOpen(true);
    }
  }, [setRightPanelOpen, setMobileRailOpen]);

  const handleAccept = async () => {
    setAcceptLoading(true);
    try {
      await customerService.acceptEstimate(publicId);
      message.success('Thank you. This estimate has been accepted.');
      await fetchEstimate();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not accept this estimate.';
      message.error(msg);
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleDeclineSubmit = async (values) => {
    setDeclineSubmitting(true);
    try {
      const payload = {};
      if (values.declined_reason && String(values.declined_reason).trim()) {
        payload.declined_reason = String(values.declined_reason).trim();
      }
      await customerService.declineEstimate(publicId, payload);
      message.success('Your response has been recorded.');
      setDeclineModalOpen(false);
      declineForm.resetFields();
      await fetchEstimate();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not decline this estimate.';
      message.error(msg);
    } finally {
      setDeclineSubmitting(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await customerService.downloadEstimatePDF(publicId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Estimate-${estimate?.estimate_number ?? publicId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* server PDF not yet available */
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <PageShell>
        <Skeleton active paragraph={{ rows: 12 }} />
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <Alert
          type="error"
          message={error}
          showIcon
          action={<Button size="small" variant="filled" onClick={fetchEstimate}>Retry</Button>}
        />
      </PageShell>
    );
  }

  if (!estimate) return null;

  const currency = getCurrencyCode(estimate.currency);
  const statusLower = String(estimate.status).toLowerCase();
  const isAccepted = ['accepted', 'converted'].includes(statusLower);
  const isDeclined = statusLower === 'declined';
  const isSent = ['sent', 'viewed', 'active'].includes(statusLower);
  const showDueDate = isDueDateDisplayed(estimate.settings);
  const isExpired = showDueDate && !isAccepted && estimate.date_due && dayjs(estimate.date_due).isBefore(dayjs(), 'day');
  const canRespond = statusLower === 'active' && !isExpired;
  const lineItems = estimate.items ?? [];

  const billingCustomer = Array.isArray(estimate.customers)
    ? estimate.customers.find((c) => c.type === 'billing') ?? estimate.customers[0]
    : null;

  return (
    <PageShell>
      <PageHeader
        title={`Estimate ${estimate.estimate_number ?? ''}`}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Estimates', path: '/estimates' },
          { label: estimate.estimate_number ?? publicId },
        ]}
      />

      <DocumentDetailActionBar
        onBack={() => navigate('/estimates')}
        onShare={() => setShareOpen(true)}
        onDownload={handleDownload}
        downloading={downloading}
        onWorkspace={handleOpenWorkspace}
        workspaceUnread={workspaceUnread}
        workspaceActive={rightPanelOpen}
      />

      <DocumentShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        variant="estimate"
      />

      {isAccepted && (
        <Alert
          type="success"
          icon={<CheckCircleFilled />}
          showIcon
          message={<Text strong>This estimate has been accepted.</Text>}
          style={{ marginBottom: 20, borderRadius: 6 }}
        />
      )}
      {isDeclined && (
        <Alert
          type="warning"
          showIcon
          message={<Text strong>This estimate was declined.</Text>}
          description={
            estimate.extra?.declined?.declined_reason ? (
              <Text type="secondary">{estimate.extra.declined.declined_reason}</Text>
            ) : null
          }
          style={{ marginBottom: 20, borderRadius: 6 }}
        />
      )}
      {isExpired && !isDeclined && (
        <Alert
          type="warning"
          icon={<ClockCircleOutlined />}
          showIcon
          message={<Text strong>This estimate has expired. Contact your account manager for a renewal.</Text>}
          style={{ marginBottom: 20, borderRadius: 6 }}
        />
      )}
      {isSent && !isExpired && !canRespond && !isAccepted && !isDeclined && (
        <Alert
          type="info"
          showIcon
          message={<Text>Updates to this estimate are managed by your account manager.</Text>}
          style={{ marginBottom: 20, borderRadius: 6 }}
        />
      )}

      <Row gutter={[16, 16]} align="top">
        <Col xs={24} lg={8} xl={7}>
          <DocumentProgressSteps
            variant="estimate"
            document={estimate}
            onDownloadPdf={handleDownload}
            downloading={downloading}
            layout="sidebar"
          />
        </Col>
        <Col xs={24} lg={16} xl={17}>
          <Card style={{ borderRadius: 8, border: '1px solid var(--varexe-border-color)', boxShadow: 'var(--varexe-box-shadow-sm)' }}>
            <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12} style={{ marginBottom: 24 }}>
              <Flex vertical style={{ gap: 6 }}>
                <Title level={3} style={{ margin: 0, fontFamily: 'Poppins, sans-serif' }}>
                  {estimate.estimate_number}
                </Title>
                <EstimateStatusBadge status={estimate.status} />
              </Flex>
              <Flex vertical align="flex-end">
                <Text style={{ fontSize: 28, fontWeight: 700, fontFamily: 'Poppins, sans-serif', color: 'var(--varexe-gray-900)' }}>
                  {formatCurrency(estimate.amount_total, currency)}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>Total Amount</Text>
              </Flex>
            </Flex>

            <Divider style={{ margin: '16px 0' }} />

            <Row gutter={[24, 16]} style={{ marginBottom: 24 }}>
              {billingCustomer && (
                <Col xs={24} md={12}>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
                    Prepared For
                  </Text>
                  <Text strong style={{ display: 'block', fontSize: 15 }}>
                    {billingCustomer.full_name ?? billingCustomer.business_name ?? billingCustomer.name ?? '—'}
                  </Text>
                  {billingCustomer.email && <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>{billingCustomer.email}</Text>}
                </Col>
              )}
              <Col xs={24} md={billingCustomer ? 12 : 24}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>
                  Details
                </Text>
                <Descriptions
                  column={1}
                  size="small"
                  colon={false}
                  labelStyle={{ color: 'var(--varexe-gray-500)', fontSize: 12, width: '46%', paddingBottom: 6 }}
                  contentStyle={{ paddingBottom: 6 }}
                >
                  <Descriptions.Item label="Estimate Date">
                    {estimate.date_issued ? dayjs(estimate.date_issued).format('MMM D, YYYY') : '—'}
                  </Descriptions.Item>
                  {showDueDate && (
                    <Descriptions.Item label="Expiry Date">
                      <Text style={{ color: isExpired ? 'var(--varexe-danger)' : undefined, fontWeight: isExpired ? 600 : 400 }}>
                        {estimate.date_due ? dayjs(estimate.date_due).format('MMM D, YYYY') : '—'}
                      </Text>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Status">
                    <EstimateStatusBadge status={estimate.status} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Currency">{currency}</Descriptions.Item>
                </Descriptions>

                {canRespond && (
                  <Flex
                    vertical
                    gap="small"
                    style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--varexe-border-color)' }}
                  >
                    <Button
                      type="primary"
                      block
                      loading={acceptLoading}
                      onClick={handleAccept}
                      style={{ borderRadius: 6 }}
                    >
                      Accept estimate
                    </Button>
                    <Button
                      variant="filled"
                      danger
                      block
                      onClick={() => setDeclineModalOpen(true)}
                      style={{ borderRadius: 6 }}
                    >
                      Decline estimate
                    </Button>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4, textAlign: 'center' }}>
                      Your response is shared with your account manager.
                    </Text>
                  </Flex>
                )}
              </Col>
            </Row>

            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>
              Items
            </Text>
            <Table
              dataSource={lineItems}
              rowKey={(r, i) => r.id ?? i}
              pagination={false}
              scroll={{ x: 'max-content' }}
              size="small"
              style={{ marginBottom: 24 }}
              columns={[
                {
                  title: 'Description',
                  dataIndex: 'name',
                  key: 'name',
                  render: (val, r) => (
                    <DocumentLineDescription name={val} description={r.description} />
                  ),
                },
                {
                  title: 'Qty',
                  dataIndex: 'quantity',
                  key: 'qty',
                  align: 'center',
                  width: 70,
                  render: (val) => val ?? '—',
                },
                {
                  title: 'Unit Price',
                  dataIndex: 'unit_price',
                  key: 'unit_price',
                  align: 'right',
                  width: 110,
                  render: (val) => formatCurrency(val, currency),
                },
                {
                  title: 'Total',
                  dataIndex: 'total_net',
                  key: 'total_net',
                  align: 'right',
                  width: 110,
                  render: (val, r) => {
                    const total = val ?? r.total_gross ?? (r.quantity * r.unit_price);
                    return <Text strong>{formatCurrency(total, currency)}</Text>;
                  },
                },
              ]}
              locale={{ emptyText: 'No line items' }}
            />

            <Flex vertical style={{ maxWidth: 320, marginLeft: 'auto' }}>
              <TotalRow label="Subtotal" value={formatCurrency(estimate.amount_subtotal, currency)} />
              {estimate.amount_item_discount_total > 0 && (
                <TotalRow label="Discount" value={`− ${formatCurrency(estimate.amount_item_discount_total, currency)}`} color="var(--varexe-success)" />
              )}
              {estimate.amount_tax_total > 0 && (
                <TotalRow label="Tax" value={formatCurrency(estimate.amount_tax_total, currency)} />
              )}
              {estimate.shipping_amount > 0 && (
                <TotalRow label="Shipping" value={formatCurrency(estimate.shipping_amount, currency)} />
              )}
              <Flex vertical style={{ padding: '10px 0' }}>
                <TotalRow label="Total" value={formatCurrency(estimate.amount_total, currency)} bold size={15} />
              </Flex>
            </Flex>

            {estimate.settings?.estimate_notes && (
              <>
                <Divider />
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
                  Notes
                </Text>
                <Paragraph style={{ fontSize: 13, color: 'var(--varexe-gray-700)' }}>{estimate.settings.estimate_notes}</Paragraph>
              </>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        title="Decline this estimate"
        open={declineModalOpen}
        onCancel={() => {
          setDeclineModalOpen(false);
          declineForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={declineForm} layout="vertical" onFinish={handleDeclineSubmit}>
          <Form.Item
            label="Reason (optional)"
            name="declined_reason"
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || String(value).trim() === '') {
                    return Promise.resolve();
                  }
                  if (String(value).trim().length < 3) {
                    return Promise.reject(new Error('If you add a reason, use at least 3 characters.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.TextArea rows={3} maxLength={2000} showCount placeholder="Optional note for your account manager" />
          </Form.Item>
          <Flex justify="end" gap={8} wrap="wrap">
            <Button
              onClick={() => {
                setDeclineModalOpen(false);
                declineForm.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" danger htmlType="submit" loading={declineSubmitting}>
              Decline estimate
            </Button>
          </Flex>
        </Form>
      </Modal>
    </PageShell>
  );
};

export default EstimateDetailPage;
